import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, TextField, Button,
  Alert, Divider, Chip, InputAdornment, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Fade, Tooltip,
} from '@mui/material';
import QrCode2Icon           from '@mui/icons-material/QrCode2';
import VisibilityIcon        from '@mui/icons-material/Visibility';
import VisibilityOffIcon     from '@mui/icons-material/VisibilityOff';
import SettingsIcon          from '@mui/icons-material/Settings';
import InstallMobileIcon     from '@mui/icons-material/InstallMobile';
import DeleteForeverIcon     from '@mui/icons-material/DeleteForever';
import CheckCircleIcon       from '@mui/icons-material/CheckCircle';
import CloseIcon             from '@mui/icons-material/Close';
import MoreVertIcon          from '@mui/icons-material/MoreVert';
import IosShareIcon          from '@mui/icons-material/IosShare';
import AddToHomeScreenIcon   from '@mui/icons-material/AddToHomeScreen';
import OpenInBrowserIcon     from '@mui/icons-material/OpenInBrowser';
import ContentCopyIcon       from '@mui/icons-material/ContentCopy';
import { getAuthToken, getLogedInUser } from '../../services/authSession';
import KioskScreen from './KioskScreen';
import KioskScanScreen from './KioskScanScreen';

const LS_KEY_TOKEN  = 'hr_kiosk_token';
const LS_KEY_DEVICE = 'hr_kiosk_device_id';
const LS_KEY_NAME   = 'hr_kiosk_device_name';
// Kompania e kioskut (slug). Id-të e kioskëve nisin nga 1 te çdo kompani, ndaj
// pa të serveri e kërkon kioskun te kompania e parazgjedhur. Bosh = ajo.
const LS_KEY_TENANT = 'hr_kiosk_tenant';
// 'display' = kiosku shfaq QR-në e vet, telefoni e skanon (sjellja e vjetër)
// 'scan'    = kiosku e lexon QR Pass-in e punëtorit me kamerë
const LS_KEY_MODE   = 'hr_kiosk_mode';
const API_URL = process.env.REACT_APP_API_URL || '';

// Pa token kioski nga build-i (REACT_APP_KIOSK_TOKEN), me qëllim: çdo
// REACT_APP_* del në bundle-in publik, dhe ai i kioskut 1 i lejonte kujtdo ta
// lexonte QR-në e kioskut nga shtëpia. Tableti i pakonfiguruar sheh formën.

/**
 * Slug-u siç e pranon serveri: shkronja të vogla, hapësirat/nënvizat → «-».
 *
 * Te FAR Logistics aktivizimi binte me «Kompania nuk njihet» sepse u shkrua
 * emri («FAR Logistics»), jo slug-u («far-logistics»). Emri i kompanisë është
 * ajo që njeriu di; slug-u del prej tij.
 */
function cleanTenant(v) {
  return String(v || '').trim().toLowerCase().replace(/[\s_]+/g, '-');
}

/**
 * Kompania që mban vetë token-i (`nxk.<slug>.<64 hex>`), ose '' për një token
 * të vjetër. Kur token-i e ka, ajo fiton mbi fushën «Kompania» dhe mbi çdo gjë
 * të ruajtur — serveri gjithsesi e refuzon një header që s'përputhet me të.
 */
function tokenCompany(token) {
  const m = /^nxk\.([a-z0-9_-]+)\.[0-9a-f]{64}$/.exec(String(token || '').trim());
  return m ? m[1] : '';
}

// ── PWA uninstall: pastro çdo gjë dhe riregjistro SW ─────────────────────────
async function fullUninstall() {
  // 1. localStorage
  [LS_KEY_TOKEN, LS_KEY_DEVICE, LS_KEY_NAME, LS_KEY_TENANT].forEach(k => localStorage.removeItem(k));

  // 2. sessionStorage
  sessionStorage.clear();

  // 3. IndexedDB (çdo database e krijuar)
  if (window.indexedDB?.databases) {
    const dbs = await window.indexedDB.databases().catch(() => []);
    await Promise.all(dbs.map(db => {
      return new Promise(res => {
        const req = window.indexedDB.deleteDatabase(db.name);
        req.onsuccess = res; req.onerror = res;
      });
    }));
  }

  // 4. Cache API — fshi të gjitha cache-t
  if ('caches' in window) {
    const keys = await caches.keys().catch(() => []);
    await Promise.all(keys.map(k => caches.delete(k)));
  }

  // 5. Service Worker — çregjistro
  if ('serviceWorker' in navigator) {
    const regs = await navigator.serviceWorker.getRegistrations().catch(() => []);
    await Promise.all(regs.map(r => r.unregister()));
  }
}

// ── Detekto browserin ─────────────────────────────────────────────────────────
function detectBrowser() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Edg\//.test(ua)) return 'edge';
  if (/Chrome/.test(ua)) return 'chrome';
  if (/Firefox/.test(ua)) return 'firefox';
  return 'other';
}

// ── Komponenta e instalimit ───────────────────────────────────────────────────
function PWAInstallSection({ installed, installPrompt, installing, onInstall }) {
  const [copied, setCopied]   = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const browser = detectBrowser();
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const kioskUrl = `${window.location.origin}/#/kiosk`;

  function copyUrl() {
    navigator.clipboard.writeText(kioskUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Tashmë e instaluar
  if (installed || isStandalone) {
    return (
      <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2, borderRadius: 2 }}>
        <Typography variant="body2" fontWeight={700}>Aplikacioni është i instaluar!</Typography>
        <Typography variant="caption" color="text.secondary">
          Kiosku po ekzekutohet si aplikacion i pavarur (PWA).
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* Butoni kryesor i instalimit (nëse browseri e mbështet) */}
      {installPrompt ? (
        <Button
          variant="contained" fullWidth size="large"
          onClick={onInstall} disabled={installing}
          startIcon={installing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <InstallMobileIcon />}
          sx={{
            mb: 1.5, py: 1.4, borderRadius: 2.5, fontWeight: 700,
            background: 'linear-gradient(90deg, #059669, #10b981)',
            boxShadow: '0 4px 14px rgba(5,150,105,0.35)',
            '&:hover': { background: 'linear-gradient(90deg, #047857, #059669)' },
          }}
        >
          {installing ? 'Duke instaluar…' : 'Instalo Kioskin si Aplikacion'}
        </Button>
      ) : (
        <Box sx={{
          border: '2px dashed', borderColor: 'primary.light',
          borderRadius: 2.5, p: 2, mb: 1.5,
          bgcolor: 'rgba(79,70,229,0.03)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InstallMobileIcon sx={{ color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={700} color="primary.main">
              Instalo manualisht si PWA
            </Typography>
          </Box>

          {/* URL e kiosk-ut me buton kopjimi */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'grey.100', borderRadius: 1.5, px: 1.5, py: 0.8, mb: 1.5,
          }}>
            <Typography variant="caption" sx={{ flex: 1, fontFamily: 'monospace', color: 'text.secondary' }}>
              {kioskUrl}
            </Typography>
            <Tooltip title={copied ? 'U kopjua!' : 'Kopjo URL-në'}>
              <IconButton size="small" onClick={copyUrl}>
                {copied ? <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Udhëzime sipas browserit */}
          {browser === 'ios' ? (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                Hapat për Safari (iPhone/iPad):
              </Typography>
              {[
                { icon: <IosShareIcon sx={{ fontSize: 16 }} />, text: 'Shtyp butonin Share (kutia me shigjetë lart)' },
                { icon: <AddToHomeScreenIcon sx={{ fontSize: 16 }} />, text: 'Zgjedh "Add to Home Screen"' },
                { icon: <CheckCircleIcon sx={{ fontSize: 16 }} />, text: 'Shtyp "Add" — ikona shfaqet në ekran' },
              ].map((step, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ color: 'primary.main' }}>{step.icon}</Box>
                  <Typography variant="caption">{step.text}</Typography>
                </Box>
              ))}
            </Box>
          ) : browser === 'chrome' || browser === 'edge' ? (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                Hapat për {browser === 'edge' ? 'Microsoft Edge' : 'Google Chrome'}:
              </Typography>
              {[
                { icon: <MoreVertIcon sx={{ fontSize: 16 }} />, text: 'Shtyp menunë ⋮ (tre pikat) lart djathtas' },
                { icon: <AddToHomeScreenIcon sx={{ fontSize: 16 }} />, text: 'Zgjedh "Install app" ose "Instalo aplikacionin"' },
                { icon: <CheckCircleIcon sx={{ fontSize: 16 }} />, text: 'Konfirmo — aplikacioni instalohet automatikisht' },
              ].map((step, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ color: 'primary.main' }}>{step.icon}</Box>
                  <Typography variant="caption">{step.text}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Hap URL-në e mësipërme nga Chrome ose Edge për instalim si PWA.
            </Typography>
          )}
        </Box>
      )}

      {/* Butoni: Hap Kioskin në tab të ri (për instalim nga Chrome) */}
      <Button
        variant="outlined" fullWidth size="medium"
        href={kioskUrl} target="_blank"
        startIcon={<OpenInBrowserIcon />}
        sx={{
          borderRadius: 2, fontWeight: 600, borderWidth: 1.5,
          '&:hover': { borderWidth: 1.5 },
        }}
      >
        Hap Kioskin në tab të ri
      </Button>
    </Box>
  );
}

// ── Komponenta kryesore ───────────────────────────────────────────────────────
export default function KioskSetup() {
  const [token,     setToken]     = useState('');
  const [deviceId,  setDeviceId]  = useState('');
  const [devName,   setDevName]   = useState('');
  const [tenant,    setTenant]    = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [error,     setError]     = useState('');
  const [testing,   setTesting]   = useState(false);
  // Aktivizimi me llogari: email/fjalëkalim, dhe kompania kur adresa ka disa.
  const [accEmail,  setAccEmail]  = useState('');
  const [accPass,   setAccPass]   = useState('');
  const [accChoices, setAccChoices] = useState(null); // [{ slug, name }] kur duhet zgjedhur
  const [showManual, setShowManual] = useState(false);
  const [ready,     setReady]     = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [kioskMode, setKioskMode] = useState(
    () => localStorage.getItem(LS_KEY_MODE) || 'display'
  );

  // PWA install
  const [installPrompt,   setInstallPrompt]   = useState(null);
  const [installed,       setInstalled]       = useState(false);
  const [installing,      setInstalling]      = useState(false);

  // Uninstall dialog
  const [confirmOpen,     setConfirmOpen]     = useState(false);
  const [uninstalling,    setUninstalling]    = useState(false);

  // Tap sekret (5 herë) për settings kur kiosk aktiv
  const tapCount = useRef(0);
  const tapTimer = useRef(null);

  // ── Kap beforeinstallprompt ──────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    // Kontrollo nëse tashmë është e instaluar (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  // ── Ngarko konfigurimet ──────────────────────────────────────────────────
  // Vetëm konfigurimi i ruajtur te pajisja. Pa të, forma.
  //
  // Hapja e faqes nuk aktivizon më asgjë vetë. Aktivizimi automatik me sesionin
  // e hapur krijonte një kiosk të ri sa herë një admin hapte #/kiosk në një
  // shfletues pa kioskun e vet — te FAR Logistics u bënë 6 më 13.10. Tani kioska
  // lidhet vetëm me një veprim: «Hap kioskun në këtë pajisje» te Kompania →
  // Kiosk QR, ose butonat e aktivizimit këtu.
  useEffect(() => {
    const t = localStorage.getItem(LS_KEY_TOKEN);
    const d = localStorage.getItem(LS_KEY_DEVICE);
    const n = localStorage.getItem(LS_KEY_NAME);
    // Kioskat e konfiguruara para kësaj s'kanë kompani → e parazgjedhura, si më parë.
    const c = tokenCompany(t) || localStorage.getItem(LS_KEY_TENANT) || '';
    if (t && d) {
      setToken(t); setDeviceId(d); setTenant(c);
      setDevName(n || 'Kiosk');
      setReady(true);
    }
  }, []);

  // ── Aktivizo kioskin ─────────────────────────────────────────────────────
  async function handleActivate() {
    setError('');
    if (!token.trim() || !deviceId.trim()) {
      setError('Plotëso Token-in dhe Device ID.');
      return;
    }
    setTesting(true);
    const company = tokenCompany(token) || cleanTenant(tenant);
    try {
      // Pajisja vërtetohet me token-in e vet, te kompania e zgjedhur — jo nga
      // lista publike, që s'e shihte fare token-in.
      const res  = await fetch(`${API_URL}/api/qr/kiosk/whoami`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Kiosk-Token': token.trim(),
          ...(company ? { 'X-Kiosk-Tenant': company } : {}),
        },
        body: JSON.stringify({ device_id: parseInt(deviceId, 10) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!data.success) {
        const messages = {
          UNKNOWN_TENANT:               `Kompania «${company}» nuk njihet ose nuk është aktive.`,
          KIOSK_TOKEN_OTHER_COMPANY:    'Ky token i takon një kompanie tjetër.',
          TENANT_REGISTRY_UNAVAILABLE:  'Serveri nuk arrin t\'i lexojë kompanitë. Provo sërish pas pak.',
          DEVICE_NOT_FOUND_OR_INACTIVE: `Device ID ${deviceId} nuk u gjet${company ? ` te «${company}»` : ''} ose është çaktivizuar.`,
          // Pa kompani, token-i kërkohet te kompania kryesore — dhe aty s'vlen
          // kurrë token-i i një kioski të një kompanie tjetër.
          INVALID_KIOSK_TOKEN:          company
            ? `Token-i nuk përputhet me kioskun ${deviceId} të «${company}».`
            : 'Token-i nuk përputhet me këtë kiosk. Nëse kiosku i takon një kompanie tjetër, plotëso fushën «Kompania».',
        };
        setError(messages[data.error] || data.message || 'Serveri nuk u përgjigj.');
        return;
      }

      localStorage.setItem(LS_KEY_TOKEN,  token.trim());
      localStorage.setItem(LS_KEY_DEVICE, String(deviceId));
      localStorage.setItem(LS_KEY_NAME,   data.device.device_name);
      if (company) localStorage.setItem(LS_KEY_TENANT, company);
      else localStorage.removeItem(LS_KEY_TENANT);
      setTenant(company);
      setDevName(data.device.device_name);
      setCompanyName(data.company_name || data.company || '');
      setReady(true);
      setShowSetup(false);
    } catch (e) {
      setError('Nuk ka lidhje: ' + e.message);
    } finally {
      setTesting(false);
    }
  }

  /** Ruan kioskun e marrë nga serveri dhe e hap ekranin e QR-së. */
  function applyKiosk(d) {
    const company = tokenCompany(d.kiosk_token) || cleanTenant(d.company);
    localStorage.setItem(LS_KEY_TOKEN,  d.kiosk_token);
    localStorage.setItem(LS_KEY_DEVICE, String(d.device.id));
    localStorage.setItem(LS_KEY_NAME,   d.device.device_name);
    if (company) localStorage.setItem(LS_KEY_TENANT, company);
    else localStorage.removeItem(LS_KEY_TENANT);
    setToken(d.kiosk_token);
    setDeviceId(String(d.device.id));
    setTenant(company);
    setDevName(d.device.device_name);
    setCompanyName(d.company_name || d.company || '');
    setAccPass('');
    setAccChoices(null);
    setReady(true);
    setShowSetup(false);
  }

  /**
   * Aktivizimi me llogarinë e kompanisë.
   *
   * Kompania merret nga hyrja — ajo ku të çon email-i dhe fjalëkalimi — jo nga
   * fusha të shkruara me dorë. Te FAR Logistics, Kompania + Device ID + token
   * dështuan disa herë radhazi dhe punëtorët s'skanonin dot.
   *
   * Sesioni i hyrjes s'ruhet te pajisja: përdoret vetëm për këtë kërkesë, që
   * kioska të mos mbetet e kyçur si admin.
   */
  async function activateWithAccount({ tenantSlug = null, useSession = false } = {}) {
    setError('');
    setTesting(true);
    const fail = (msg) => { setError(msg); return false; };
    try {
      let bearer = useSession ? getAuthToken() : '';
      if (!bearer) {
        if (!accEmail.trim() || !accPass) { return fail('Shkruaj email-in dhe fjalëkalimin.'); }
        const lr = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ identifier: accEmail.trim(), password: accPass, ...(tenantSlug ? { tenant: tenantSlug } : {}) }),
        });
        const ld = await lr.json().catch(() => ({}));
        if (ld?.needsCompany) {
          const list = ld?.data?.companies || [];
          if (!list.length) { return fail('Kjo llogari s\'i takon asnjë kompanie. Hyr me email-in e punës.'); }
          setAccChoices(list);
          return false;
        }
        bearer = ld?.data?.token;
        if (!lr.ok || !bearer) { return fail(ld?.error || ld?.message || 'Email-i ose fjalëkalimi është i gabuar.'); }
      }

      // Kiosku që kjo pajisje ka tashmë: serveri e rripërdor (token i ri, i
      // njëjti id) në vend që të krijojë një tjetër. Token-i i vjetër është
      // prova se pajisja është vërtet ajo — një id i huaj s'kalon.
      const ownId = localStorage.getItem(LS_KEY_DEVICE);
      const ownToken = localStorage.getItem(LS_KEY_TOKEN);
      const ar = await fetch(`${API_URL}/api/qr/kiosk/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', Authorization: `Bearer ${bearer}`,
          ...(ownId && ownToken ? { 'X-Kiosk-Token': ownToken } : {}),
        },
        body: JSON.stringify(ownId && ownToken ? { device_id: parseInt(ownId, 10) } : {}),
      });
      const ad = await ar.json().catch(() => ({}));
      if (ar.status === 403) { return fail('Kioskën e aktivizon vetëm admini ose HR i kompanisë.'); }
      if (ar.status === 401) { return fail('Sesioni ka skaduar. Hyr sërish me email dhe fjalëkalim.'); }
      if (!ad.success || !ad.kiosk_token) { return fail(ad.message || ad.error || 'Kioska nuk u aktivizua.'); }
      applyKiosk(ad);
      return true;
    } catch (e) {
      return fail('Nuk ka lidhje: ' + e.message);
    } finally {
      setTesting(false);
    }
  }

  // ── Instalo si PWA ───────────────────────────────────────────────────────
  async function handleInstall() {
    if (!installPrompt) return;
    setInstalling(true);
    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setInstalled(true);
        setInstallPrompt(null);
      }
    } finally {
      setInstalling(false);
    }
  }

  // ── Çinstalo gjithçka ────────────────────────────────────────────────────
  async function handleUninstall() {
    setUninstalling(true);
    try {
      await fullUninstall();
      // Rifresko faqen pas 800ms për efekt të pastër
      setTimeout(() => window.location.reload(), 800);
    } catch {
      setUninstalling(false);
      setConfirmOpen(false);
    }
  }

  // ── Tap sekret 5x për të hapur settings ──────────────────────────────────
  function handleSecretTap() {
    tapCount.current += 1;
    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 2000);
    if (tapCount.current >= 5) {
      tapCount.current = 0;
      setShowSetup(true);
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // Kiosk aktiv — shfaq ekranin e QR
  // ════════════════════════════════════════════════════════════════════════════
  if (ready && !showSetup) {
    return (
      <Box sx={{ position: 'relative' }}>
        {/* Zone e fshehur — 5 tap për settings */}
        <Box
          onClick={handleSecretTap}
          sx={{
            position: 'fixed', bottom: 0, right: 0,
            width: 80, height: 80, zIndex: 300,
            cursor: 'default',
          }}
        />
        {/* Butoni i fshehur i settings */}
        <Box sx={{
          position: 'fixed', bottom: 12, right: 12, zIndex: 200,
          opacity: 0.08, '&:hover': { opacity: 0.45 }, transition: 'opacity 0.3s',
        }}>
          <Tooltip title="Konfigurim" placement="left">
            <IconButton
              size="small"
              onClick={() => setShowSetup(true)}
              sx={{ bgcolor: 'grey.800', color: '#fff', '&:hover': { bgcolor: 'grey.700' } }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {kioskMode === 'scan'
          ? <KioskScanScreen kioskToken={token} kioskTenant={tenant} deviceId={parseInt(deviceId, 10)} />
          : <KioskScreen     kioskToken={token} kioskTenant={tenant} deviceId={parseInt(deviceId, 10)} />}
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // Forma e konfigurimit (Admin Panel)
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #F1F7F9 0%, #F9FAFB 100%)',
      p: 2,
    }}>
      <Fade in timeout={400}>
        <Paper elevation={8} sx={{
          p: { xs: 3, sm: 4 }, maxWidth: 460, width: '100%',
          borderRadius: 4, position: 'relative', overflow: 'hidden',
        }}>
          {/* Dekor top */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0,
            height: 4,
            background: '#3E8FA3',
          }} />

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              bgcolor: '#3E8FA3',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <QrCode2Icon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: -0.3 }}>
                Kiosk Admin Panel
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                HR Puntori — QR Check-in
              </Typography>
            </Box>
            {ready && showSetup && (
              <IconButton size="small" onClick={() => setShowSetup(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {ready && showSetup && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2, borderRadius: 2 }}>
              Aktiv: <strong>{devName}</strong> (ID: {deviceId})
              {(companyName || tenant) && <> · {companyName || tenant}</>}
            </Alert>
          )}

          {/* ── Mënyra e punës e kioskut ── */}
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Mënyra e kioskut
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 0.5, mt: 0.7 }}>
            {[
              { key: 'scan',    title: 'Skano QR Pass',  sub: 'Kiosku lexon QR-në e punëtorit' },
              { key: 'display', title: 'Shfaq QR',       sub: 'Punëtori skanon me telefon' },
            ].map(({ key, title, sub }) => (
              <Box
                key={key}
                onClick={() => { setKioskMode(key); localStorage.setItem(LS_KEY_MODE, key); }}
                sx={{
                  flex: 1, p: 1.2, borderRadius: 2, cursor: 'pointer',
                  border: '2px solid',
                  borderColor: kioskMode === key ? '#3E8FA3' : 'divider',
                  bgcolor: kioskMode === key ? 'rgba(62,143,163,0.08)' : 'transparent',
                  transition: 'all 0.18s',
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '0.84rem' }}>{title}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{sub}</Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
            "Skano QR Pass" kërkon kamerë në pajisjen e kioskut.
          </Typography>

          {/* ── Aktivizimi me llogarinë e kompanisë ── */}
          <Divider sx={{ mb: 2.5 }}>
            <Chip label="Aktivizo me llogarinë e kompanisë" size="small" sx={{ fontWeight: 600 }} />
          </Divider>

          {(() => {
            const me = getLogedInUser();
            const meEmail = me?.data?.email || me?.email || '';
            return getAuthToken() ? (
              <Button
                variant="contained" fullWidth size="large" disabled={testing}
                onClick={() => activateWithAccount({ useSession: true })}
                sx={{ mb: 1.5, py: 1.2, borderRadius: 2.5, fontWeight: 700, bgcolor: '#3E8FA3', '&:hover': { bgcolor: '#256878' } }}
              >
                {testing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `Aktivizo për kompaninë time${meEmail ? ` (${meEmail})` : ''}`}
              </Button>
            ) : null;
          })()}

          <TextField
            label="Email" value={accEmail} onChange={e => { setAccEmail(e.target.value); setAccChoices(null); }}
            fullWidth size="small" sx={{ mb: 1.5 }} type="email" autoComplete="username"
            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
          />
          <TextField
            label="Fjalëkalimi" value={accPass} onChange={e => setAccPass(e.target.value)}
            fullWidth size="small" sx={{ mb: 1.5 }} type="password" autoComplete="current-password"
            onKeyDown={e => { if (e.key === 'Enter') activateWithAccount(); }}
            helperText="Admini ose HR i kompanisë. Kioska i takon kompanisë ku të çon kjo llogari."
          />

          {accChoices && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Kjo llogari është në disa kompani — zgjidh për cilën është kioska:
              </Typography>
              {accChoices.map(c => (
                <Button key={c.slug} fullWidth variant="outlined" sx={{ mt: 0.8, textTransform: 'none' }}
                  disabled={testing} onClick={() => activateWithAccount({ tenantSlug: c.slug })}>
                  {c.name || c.slug}
                </Button>
              ))}
            </Box>
          )}

          {error && !showManual && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
          )}

          <Button
            variant="contained" fullWidth size="large"
            onClick={() => activateWithAccount()} disabled={testing}
            sx={{
              mb: 2, py: 1.4, borderRadius: 2.5, fontWeight: 700,
              bgcolor: '#3E8FA3',
              boxShadow: '0 4px 14px rgba(62,143,163,0.35)',
              '&:hover': { bgcolor: '#256878' },
            }}
          >
            {testing
              ? <><CircularProgress size={18} sx={{ color: '#fff', mr: 1 }} />Duke aktivizuar…</>
              : 'Hyr dhe aktivizo kioskën'}
          </Button>

          <Button size="small" fullWidth onClick={() => setShowManual(v => !v)} sx={{ mb: 2, textTransform: 'none' }}>
            {showManual ? 'Fshih konfigurimin manual' : 'Konfigurim manual (Device ID + token)'}
          </Button>

          {/* ── Seksioni i konfigurimit ── */}
          {showManual && <>
          <Divider sx={{ mb: 2.5 }}>
            <Chip label="Konfigurim i pajisjes" size="small" sx={{ fontWeight: 600 }} />
          </Divider>

          <TextField
            label="Kompania" value={tokenCompany(token) || tenant}
            onChange={e => setTenant(e.target.value)}
            disabled={!!tokenCompany(token)}
            fullWidth size="small" sx={{ mb: 2 }}
            placeholder="p.sh. far-logistics"
            helperText={tokenCompany(token)
              ? 'Kompania vjen nga token-i.'
              : 'Slug-u ose emri i kompanisë (p.sh. far-logistics ose FAR Logistics). Lëre bosh për kompaninë kryesore.'}
            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
          />

          <TextField
            label="Device ID" value={deviceId}
            onChange={e => setDeviceId(e.target.value)}
            fullWidth size="small" sx={{ mb: 2 }}
            type="number" helperText="Numri i kiosku-t (jepet nga admini)"
          />

          <TextField
            label="Kiosk Token" value={token}
            onChange={e => setToken(e.target.value)}
            fullWidth size="small" sx={{ mb: 2 }}
            type={showToken ? 'text' : 'password'}
            helperText="Token-i sekret i kiosku-t"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowToken(v => !v)}>
                    {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && showManual && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
          )}

          <Button
            variant="contained" fullWidth size="large"
            onClick={handleActivate} disabled={testing}
            sx={{
              mb: 2, py: 1.4, borderRadius: 2.5, fontWeight: 700,
              bgcolor: '#3E8FA3',
              boxShadow: '0 4px 14px rgba(62,143,163,0.35)',
              '&:hover': { bgcolor: '#256878' },
            }}
          >
            {testing
              ? <><CircularProgress size={18} sx={{ color: '#fff', mr: 1 }} />Duke verifikuar…</>
              : 'Aktivizo Kioskin'
            }
          </Button>
          </>}

          {/* ── Seksioni i instalimit PWA ── */}
          <Divider sx={{ mb: 2.5 }}>
            <Chip
              label="Instalo si Aplikacion"
              size="small"
              icon={<InstallMobileIcon sx={{ fontSize: '14px !important' }} />}
              sx={{ fontWeight: 600 }}
            />
          </Divider>

          <PWAInstallSection
            installed={installed}
            installPrompt={installPrompt}
            installing={installing}
            onInstall={handleInstall}
          />

          {/* ── Seksioni i çinstalimit ── */}
          <Divider sx={{ mb: 2.5 }}>
            <Chip
              label="Çinstalim & Rivendosje"
              size="small"
              sx={{ fontWeight: 600, color: 'error.main', borderColor: 'error.light' }}
              variant="outlined"
            />
          </Divider>

          <Button
            variant="outlined" color="error" fullWidth size="large"
            onClick={() => setConfirmOpen(true)}
            startIcon={<DeleteForeverIcon />}
            sx={{
              py: 1.3, borderRadius: 2.5, fontWeight: 700,
              borderWidth: 2,
              '&:hover': { borderWidth: 2, bgcolor: 'rgba(211,47,47,0.06)' },
            }}
          >
            Çinstalo & Fshij gjithçka
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            Fshin token-in, cache-t, Service Worker dhe të dhënat lokale.
          </Typography>
        </Paper>
      </Fade>

      {/* ── Dialog konfirmimi çinstalim ─────────────────────────────────────── */}
      <Dialog
        open={confirmOpen}
        onClose={() => !uninstalling && setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 380 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '50%',
            bgcolor: 'error.light', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <DeleteForeverIcon sx={{ color: 'error.main' }} />
          </Box>
          <Typography fontWeight={700}>Konfirmo çinstalimin</Typography>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Ky veprim do të:
          </Typography>
          {[
            'Fshijë token-in dhe Device ID nga memoria lokale',
            'Pastrojë të gjitha cache-t e aplikacionit',
            'Çregjistrojë Service Worker-in',
            'Fshijë të dhënat nga IndexedDB dhe sessionStorage',
          ].map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.8 }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                bgcolor: 'error.main', mt: '7px', flexShrink: 0,
              }} />
              <Typography variant="body2">{item}</Typography>
            </Box>
          ))}
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            Faqja do të rifresohet automatikisht pas çinstalimit.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={uninstalling}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Anulo
          </Button>
          <Button
            variant="contained" color="error"
            onClick={handleUninstall}
            disabled={uninstalling}
            startIcon={uninstalling
              ? <CircularProgress size={16} sx={{ color: '#fff' }} />
              : <DeleteForeverIcon />
            }
            sx={{ borderRadius: 2, fontWeight: 700, minWidth: 160 }}
          >
            {uninstalling ? 'Duke fshirë…' : 'Po, çinstalo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
